// @ts-nocheck
// Live-call feed for one scope. Primary transport is the SSE stream
// (/live-calls/stream — frames only when the snapshot changes, same auth
// scheme as useTeamRealtime); if the stream errors it falls back to polling
// GET /live-calls every POLL_MS and retries the stream later. Both pause
// while the tab is hidden. A 1 s clock drives the duration cells.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiCall } from '@/service/httpCall';
import { API_LIVE_CALLS, API_LIVE_CALLS_STREAM } from '@/service/url';
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from '@/utilities/constant';
import { getUserAccessToken } from '@/utilities/setting';
import { usePageVisible } from '@/hooks/usePageVisible';
import type { LiveCallsPage, LiveCallsQuery } from './types';

const POLL_MS = 3000;
const STREAM_RETRY_MS = 15000;
const STREAM_BASE = `${BACKEND_API_BASE_URL.replace(/\/+$/, '')}${API_LIVE_CALLS_STREAM}`;

function cleanQuery(query: LiveCallsQuery | null | undefined) {
  const out: Record<string, string> = {};
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    out[k] = String(v);
  });
  return out;
}

export interface UseLiveCallsResult {
  data: LiveCallsPage | null;
  loading: boolean;
  error: string | null;
  /** 'stream' | 'poll' | 'off' */
  transport: 'stream' | 'poll' | 'off';
  /** Client clock, ticking every second while visible. */
  now: number;
  refresh: () => Promise<void>;
}

export function useLiveCalls(query: LiveCallsQuery | null, enabled = true): UseLiveCallsResult {
  const [data, setData] = useState<LiveCallsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transport, setTransport] = useState<'stream' | 'poll' | 'off'>('off');
  const [now, setNow] = useState(() => Date.now());
  const pageVisible = usePageVisible();
  const active = enabled && Boolean(query) && pageVisible;

  const params = useMemo(() => cleanQuery(query), [JSON.stringify(query || {})]);
  const paramsKey = JSON.stringify(params);
  const serverOffsetRef = useRef(0);

  const apply = useCallback((page: LiveCallsPage) => {
    if (page?.now) serverOffsetRef.current = page.now - Date.now();
    setData(page);
    setError(null);
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    if (!query) return;
    try {
      const page = await getApiCall(API_LIVE_CALLS, params);
      if (page) apply(page);
    } catch (err) {
      setError(typeof err === 'string' ? err : err?.message || 'Unable to load live calls');
      setLoading(false);
    }
  }, [paramsKey, apply, Boolean(query)]);

  // Clock.
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);

  // Transport.
  useEffect(() => {
    if (!active) {
      setTransport('off');
      return undefined;
    }
    setLoading((l) => l || !data);
    let source: EventSource | null = null;
    let pollTimer: any = null;
    let retryTimer: any = null;
    let closed = false;

    const stopPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    };
    const startPolling = () => {
      if (pollTimer) return;
      setTransport('poll');
      refresh();
      pollTimer = setInterval(refresh, POLL_MS);
    };

    const connect = () => {
      if (closed) return;
      const token = getUserAccessToken();
      const qs = new URLSearchParams({
        ...params,
        token: token || '',
        'x-client-source': CLIENT_SOURCE,
      });
      source = new EventSource(`${STREAM_BASE}?${qs.toString()}`);
      source.onopen = () => {
        stopPolling();
        setTransport('stream');
      };
      source.addEventListener('calls', (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data)?.data;
          if (payload) apply(payload);
        } catch {
          /* malformed frame — the next one replaces it */
        }
      });
      source.addEventListener('error', (event: MessageEvent) => {
        // Scope errors arrive as a named event with a JSON body before close.
        try {
          const body = event?.data ? JSON.parse(event.data) : null;
          if (body?.message) setError(body.message);
        } catch {
          /* transport error, no body */
        }
      });
      source.onerror = () => {
        if (source) source.close();
        source = null;
        if (closed) return;
        startPolling();
        clearTimeout(retryTimer);
        retryTimer = setTimeout(connect, STREAM_RETRY_MS);
      };
    };

    if (typeof EventSource === 'undefined') startPolling();
    else connect();

    return () => {
      closed = true;
      clearTimeout(retryTimer);
      stopPolling();
      if (source) source.close();
      setTransport('off');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, paramsKey]);

  return { data, loading, error, transport, now: now + serverOffsetRef.current, refresh };
}

export default useLiveCalls;
